import { Grid, Card, CardContent, Typography, Box, LinearProgress, Chip } from '@mui/material';
import {
  Assignment as TotalIcon,
  CheckCircle as CompletedIcon,
  Timeline as ProgressIcon,
  VerifiedUser as QualityIcon,
  PlayArrow as InProgressIcon,
  PauseCircleOutline as NotStartedIcon,
  Cancel as CancelledIcon,
} from '@mui/icons-material';
import { HorizontalForm, LoadingSkeleton } from '../../../../common/components';

/**
 * Componente de resumen estadístico general del dashboard
 * Muestra métricas agregadas de todos los proyectos
 */
const DashboardStatsOverview = ({ stats, isLoading }) => {
  if (isLoading) {
    return <LoadingSkeleton variant="cards" />;
  }

  const statsCards = [
    {
      title: 'Total Proyectos',
      value: stats.totalProjects,
      icon: TotalIcon,
      color: '#2196f3',
      bgcolor: '#e3f2fd',
      subtitle: 'Proyectos registrados',
    },
    {
      title: 'Completados',
      value: stats.completedProjects,
      icon: CompletedIcon,
      color: '#4caf50',
      bgcolor: '#e8f5e8',
      subtitle: `${stats.completionRate}% del total`,
      showProgress: true,
      progressValue: parseFloat(stats.completionRate),
    },
    {
      title: 'En Progreso',
      value: stats.inProgressProjects,
      icon: InProgressIcon,
      color: '#ff9800',
      bgcolor: '#fff3e0',
      subtitle: 'Activos actualmente',
    },
    {
      title: 'Sin Empezar',
      value: stats.notStartedProjects,
      icon: NotStartedIcon,
      color: '#9e9e9e',
      bgcolor: '#f5f5f5',
      subtitle: 'Pendientes de inicio',
    },
    {
      title: 'Cancelados',
      value: stats.cancelledProjects,
      icon: CancelledIcon,
      color: '#f44336',
      bgcolor: '#ffebee',
      subtitle: 'Proyectos cancelados',
    },
    {
      title: 'Progreso Promedio',
      value: `${stats.avgProgress}%`,
      icon: ProgressIcon,
      color: '#9c27b0',
      bgcolor: '#f3e5f5',
      subtitle: 'Avance general',
      showProgress: true,
      progressValue: parseFloat(stats.avgProgress),
    },
    {
      title: 'Calidad Promedio',
      value: `${stats.avgQuality}%`,
      icon: QualityIcon,
      color: '#00bcd4',
      bgcolor: '#e0f2f1',
      subtitle: 'Avance ponderado',
      showProgress: true,
      progressValue: parseFloat(stats.avgQuality),
    },
  ];

  return (
    <HorizontalForm variant="twoRows">
      <Grid container sx={{ justifyContent: 'space-between' }}>
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;

          return (
            <Grid item xs={12} sm={6} md={3} lg={1.5} key={index}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-4px)',
                    borderColor: stat.color,
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                      >
                        {stat.title}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 'bold',
                          color: 'text.primary',
                          fontSize: '1.5rem',
                          mt: 0.5,
                        }}
                      >
                        {stat.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: stat.bgcolor,
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 36,
                        minHeight: 36,
                      }}
                    >
                      <Icon sx={{ color: stat.color, fontSize: '1.2rem' }} />
                    </Box>
                  </Box>

                  {/* Subtítulo */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: stat.showProgress ? 1 : 0 }}
                  >
                    {stat.subtitle}
                  </Typography>

                  {/* Progress bar si está definida */}
                  {stat.showProgress && (
                    <Box>
                      <LinearProgress
                        variant="determinate"
                        value={stat.progressValue}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: stat.color,
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Box display="flex" justifyContent="space-between" mt={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          0%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          100%
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Resumen adicional */}
      <Card sx={{ mt: 3 }} elevation={3}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <Typography variant="h6" color="primary" gutterBottom>
                Estado General
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {stats.inProgressProjects > 0 && (
                  <Chip
                    label={`${stats.inProgressProjects} Activos`}
                    color="warning"
                    size="small"
                  />
                )}
                {stats.completedProjects > 0 && (
                  <Chip
                    label={`${stats.completedProjects} Completados`}
                    color="success"
                    size="small"
                  />
                )}
                {stats.notStartedProjects > 0 && (
                  <Chip
                    label={`${stats.notStartedProjects} Pendientes`}
                    color="default"
                    size="small"
                  />
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tasa de Finalización
              </Typography>
              <Typography variant="h5" color="success.main">
                {stats.completionRate}%
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Progreso Promedio
              </Typography>
              <Typography variant="h5" color="primary.main">
                {stats.avgProgress}%
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Calidad Promedio
              </Typography>
              <Typography variant="h5" color="info.main">
                {stats.avgQuality}%
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </HorizontalForm>
  );
};

export default DashboardStatsOverview;
